"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useOffline } from "next/offline";
import { Radar, type NucleusState } from "@/components/radar";
import { InstallButton } from "@/components/install-button";
import { PttButton } from "@/components/ptt-button";
import { HudBrackets } from "@/components/hud-brackets";
import { HudPanel } from "@/components/hud-panel";
import { HudBar } from "@/components/hud-bar";
import {
  ASSISTANT_NAME,
  CHAT_CLOSE_PATTERNS,
  CHAT_OPEN_PATTERNS,
  FOLLOWUP_FAREWELLS,
  IDLE_FAREWELLS,
  NUCLEUS_ERROR_FAREWELL,
  SPEECH_LANG,
  USER_NAME,
  WAKE_ACK,
} from "@/lib/constants";
import {
  type AssistantAction,
  type CalendarAction,
  type EmailAction,
  buildCalendarUrl,
  buildMailto,
} from "@/lib/actions";
import { openSSE } from "@/lib/sse-client";
import { segmentText } from "@/lib/sentence-segmenter";
import { AudioQueue } from "@/lib/audio-queue";

type Role = "user" | "assistant" | "system";

interface ChatMessage {
  id: string;
  role: Role;
  content: string;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<{
    isFinal: boolean;
    0: { transcript: string };
  }>;
}

declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  }
}

const WAKE_PATTERNS: RegExp[] = [
  /\bg[oó]rdo\b/i,
  /\bgord[oó]n\b/i,
  /\bo[íi]\s+g[oó]rdo\b/i,
  /\bdiga\s+(?:a\s+)?g[oó]rdo\b/i,
  /\bg\s*[oó]\s*r\s*d\s*o\b/i,
];

function detectWakeWord(text: string): boolean {
  return WAKE_PATTERNS.some((p) => p.test(text));
}

const COMMAND_SILENCE_MS = 2000;
const FOLLOWUP_TIMEOUT_MS = 10000;
const WAKE_FLASH_MS = 700;

type Phase = "always" | "command" | "followup";

function rmsFromTimeDomain(buf: Uint8Array): number {
  let sum = 0;
  for (let i = 0; i < buf.length; i++) {
    const v = (buf[i] - 128) / 128;
    sum += v * v;
  }
  return Math.sqrt(sum / buf.length);
}

function newId() {
  return Math.random().toString(36).slice(2, 10);
}

function pickRandom<T>(arr: readonly T[], previous?: T | null): T {
  if (arr.length < 2 || previous === undefined || previous === null) {
    return arr[Math.floor(Math.random() * arr.length)];
  }
  let next = arr[Math.floor(Math.random() * arr.length)];
  while (next === previous) {
    next = arr[Math.floor(Math.random() * arr.length)];
  }
  return next;
}

/**
 * Fábrica de un AsyncIterable<string> con push()/close() controlado por el
 * productor. El consumidor (segmenter) usa for-await; mientras no haya datos
 * ni close, las llamadas a next() quedan esperando.
 */
function createTextStream() {
  const queue: string[] = [];
  const waiters: Array<(v: string | null) => void> = [];
  let closed = false;

  return {
    push(chunk: string) {
      if (closed) return;
      const w = waiters.shift();
      if (w) w(chunk);
      else queue.push(chunk);
    },
    close() {
      closed = true;
      while (waiters.length) {
        const w = waiters.shift();
        w?.(null);
      }
    },
    isClosed() {
      return closed;
    },
    [Symbol.asyncIterator]() {
      return {
        next: (): Promise<IteratorResult<string>> => {
          if (queue.length) {
            return Promise.resolve({ value: queue.shift()!, done: false });
          }
          if (closed) {
            return Promise.resolve({ value: undefined as unknown as string, done: true });
          }
          return new Promise((resolve) => {
            waiters.push((v) => {
              if (v === null) resolve({ value: undefined as unknown as string, done: true });
              else resolve({ value: v, done: false });
            });
          });
        },
      };
    },
  };
}

export default function Home() {
  const [nucleusState, setNucleusState] = useState<NucleusState>("idle");
  const [phase, setPhase] = useState<Phase>("always");
  const [wakeFlash, setWakeFlash] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "init",
      role: "assistant",
      content: `Buenas, ${USER_NAME}. Cuando quieras, di "${ASSISTANT_NAME}" o pulsa el botón.`,
    },
  ]);
  const [action, setAction] = useState<AssistantAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [supported, setSupported] = useState(true);
  const [muted, setMuted] = useState(false);
  const [userName, setUserName] = useState<string>(USER_NAME);
  const [nameInput, setNameInput] = useState("");
  const [activated, setActivated] = useState(false);
  const [nameRequired, setNameRequired] = useState(true);
  // Toggle de escucha continua de la wake word. Default false hasta
  // hidratar desde localStorage / detectar móvil.
  const [alwaysListen, setAlwaysListen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  // En móvil, SpeechRecognition y un getUserMedia paralelo (el del
  // medidor de nivel) compiten por un micro que el SO solo cede en
  // exclusiva a un consumidor. El segundo stream le roba el micro a
  // SpeechRecognition, que entonces erroa y se reinicia en bucle. Por
  // eso en móvil no abrimos ese segundo stream (ver startMic más abajo).
  const isMobileRef = useRef(false);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const micAnalyserRef = useRef<AnalyserNode | null>(null);
  const audioAnalyserRef = useRef<AnalyserNode | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const levelRafRef = useRef<number>(0);
  const levelTickRef = useRef(0);
  const nucleusStateRef = useRef<NucleusState>("idle");
  const phaseRef = useRef<Phase>("always");
  const mutedRef = useRef(false);
  const alwaysListenRef = useRef(false);
  const submittingRef = useRef(false);
  const liveTranscriptRef = useRef("");
  const commandBufferRef = useRef("");
  const wakeBufferRef = useRef("");
  const silenceTimerRef = useRef<number | null>(null);
  const wakeFlashTimerRef = useRef<number | null>(null);
  const lastFarewellRef = useRef<string | null>(null);
  const followupTimerRef = useRef<number | null>(null);
  // true entre soltar el botón PTT y el envío real del comando: esperamos
  // al resultado final pendiente (o a onend como red de seguridad) antes
  // de mandar, para no perder la última palabra dicha justo al soltar.
  const pttPendingSendRef = useRef(false);
  // Watchdog independiente de silenceTimerRef: ese se limpia con
  // cualquier resultado interim, y si lo reutilizásemos aquí un interim
  // sin final posterior dejaría pttPendingSendRef colgado para siempre.
  const pttWatchdogTimerRef = useRef<number | null>(null);

  // Pipeline LLM→TTS: cola de audio compartida por todos los commands
  const audioQueueRef = useRef<AudioQueue | null>(null);
  const pipelineAbortRef = useRef<AbortController | null>(null);
  const streamOpenRef = useRef(false);
  const [pipelineActive, setPipelineActive] = useState(false);

  nucleusStateRef.current = nucleusState;
  phaseRef.current = phase;
  mutedRef.current = muted;
  alwaysListenRef.current = alwaysListen;

  const isOffline = useOffline();
  const offlineRef = useRef(false);
  const wasOfflineRef = useRef(false);
  const prevStateRef = useRef<NucleusState>("idle");
  offlineRef.current = isOffline;

  // Transición del ente según conectividad: guarda el estado real al
  // perder la red y lo restaura al recuperarla.
  useEffect(() => {
    if (isOffline && !wasOfflineRef.current) {
      setNucleusState((prev) => {
        prevStateRef.current = prev;
        return "offline";
      });
    } else if (!isOffline && wasOfflineRef.current) {
      setNucleusState(prevStateRef.current);
    }
    wasOfflineRef.current = isOffline;
  }, [isOffline]);

  // Al caer la red, apagar reconocimiento y micro activos.
  useEffect(() => {
    if (!isOffline) return;
    const r = recognitionRef.current;
    if (r) {
      try {
        r.abort();
      } catch {
        /* ignore */
      }
      recognitionRef.current = null;
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (followupTimerRef.current) {
      clearTimeout(followupTimerRef.current);
      followupTimerRef.current = null;
    }
    commandBufferRef.current = "";
    wakeBufferRef.current = "";
    setTranscript("");
    setPhase("always");
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
    if (micAnalyserRef.current) {
      micAnalyserRef.current.disconnect();
      micAnalyserRef.current = null;
    }
    setMicLevel(0);
  }, [isOffline]);

  useEffect(() => {
    const SR =
      typeof window !== "undefined"
        ? window.SpeechRecognition || window.webkitSpeechRecognition
        : null;
    if (!SR) setSupported(false);
  }, []);

  // Default: ON en escritorio, OFF en móvil. El userAgent cubre tablets/iphones;
  // innerWidth es la red de seguridad para ventanas estrechas en desktop.
  // Si el usuario tocó el toggle, su elección vive en localStorage con la
  // clave ":user" y manda sobre el default.
  useEffect(() => {
    const isMobile =
      /mobi|android|iphone|ipad/i.test(navigator.userAgent) ||
      window.innerWidth < 768;
    isMobileRef.current = isMobile;
    setIsMobile(isMobile);
    // En móvil la escucha continua no está disponible: solo PTT.
    if (isMobile) {
      setAlwaysListen(false);
      return;
    }
    const saved = localStorage.getItem("rigodon:alwaysListen:user");
    if (saved !== null) {
      setAlwaysListen(saved === "true");
      return;
    }
    setAlwaysListen(!isMobile);
  }, []);

  // Cargar nombre de usuario desde localStorage
  useEffect(() => {
    const savedName = localStorage.getItem("rigodon:userName");
    if (savedName && savedName.trim()) {
      setUserName(savedName.trim());
      setNameRequired(false);
    }
  }, []);

  // Actualizar mensaje inicial cuando cambia el nombre de usuario
  useEffect(() => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === "init"
          ? {
              ...m,
              content: `Buenas, ${userName}. Cuando quieras, di "${ASSISTANT_NAME}" o pulsa el botón.`,
            }
          : m
      )
    );
  }, [userName]);

  const stopLevelLoop = useCallback(() => {
    if (levelRafRef.current) {
      cancelAnimationFrame(levelRafRef.current);
      levelRafRef.current = 0;
    }
  }, []);

  const startLevelLoop = useCallback(() => {
    stopLevelLoop();
    let fakeStart = 0;
    const tick = () => {
      let mic = 0;
      let audio = 0;
      if (micAnalyserRef.current) {
        const buf = new Uint8Array(micAnalyserRef.current.fftSize);
        micAnalyserRef.current.getByteTimeDomainData(buf);
        mic = Math.min(1, rmsFromTimeDomain(buf) * 2.2);
      }
      if (audioAnalyserRef.current) {
        const buf = new Uint8Array(audioAnalyserRef.current.fftSize);
        audioAnalyserRef.current.getByteTimeDomainData(buf);
        audio = Math.min(1, rmsFromTimeDomain(buf) * 2.6);
      } else if (
        nucleusStateRef.current === "speaking" &&
        audioQueueRef.current &&
        !audioQueueRef.current.isEmpty()
      ) {
        // Respaldo mientras se inicia el analizador de la frase actual.
        if (!fakeStart) fakeStart = performance.now();
        const t = (performance.now() - fakeStart) / 1000;
        audio = 0.45 + 0.35 * Math.abs(Math.sin(t * 4.2));
      }
      levelTickRef.current = (levelTickRef.current + 1) % 3;
      if (levelTickRef.current === 0) {
        setMicLevel(mic);
        setAudioLevel(audio);
      }
      levelRafRef.current = requestAnimationFrame(tick);
    };
    levelRafRef.current = requestAnimationFrame(tick);
  }, [stopLevelLoop]);

  const ensureAudioCtx = useCallback(async (): Promise<AudioContext> => {
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      if (audioCtxRef.current.state === "suspended") {
        await audioCtxRef.current.resume();
      }
      return audioCtxRef.current;
    }
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    const ctx = new Ctx();
    audioCtxRef.current = ctx;
    if (ctx.state === "suspended") await ctx.resume();
    return ctx;
  }, []);

  const startMic = useCallback(async () => {
    // En móvil no abrimos este stream: competiría por el micro con
    // SpeechRecognition (ver isMobileRef más arriba) y lo hace entrar
    // en bucle de errores/reinicios. El medidor de nivel se queda
    // plano en móvil; es un precio aceptable frente a un micro roto.
    if (isMobileRef.current) {
      startLevelLoop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      const ctx = await ensureAudioCtx();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.4;
      source.connect(analyser);
      micAnalyserRef.current = analyser;
      startLevelLoop();
    } catch (err) {
      console.error("Mic error:", err);
      setError(
        "No pude acceder al micrófono. Revisa los permisos del navegador."
      );
    }
  }, [ensureAudioCtx, startLevelLoop]);

  const stopMic = useCallback(() => {
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
    if (micAnalyserRef.current) {
      micAnalyserRef.current.disconnect();
      micAnalyserRef.current = null;
    }
  }, []);

  // Inicializa la AudioQueue una sola vez (necesita la AudioContext que
  // puede no existir en el primer render).
  useEffect(() => {
    if (audioQueueRef.current) return;
    audioQueueRef.current = new AudioQueue();
  }, []);

  /**
   * TTS streaming → devuelve un Blob MP3 ya montado. Se llama por frase
   * desde el segmenter; el AudioQueue encadena los blobs sin pausas.
   */
  const callSpeakBlob = useCallback(async (text: string): Promise<Blob> => {
    const res = await fetch("/api/speak", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
    });
    if (!res.ok || !res.body) {
      throw new Error(`TTS error ${res.status}`);
    }
    const reader = res.body.getReader();
    const chunks: Uint8Array[] = [];
    let totalLen = 0;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        chunks.push(value);
        totalLen += value.byteLength;
      }
    }
    const combined = new Uint8Array(totalLen);
    let offset = 0;
    for (const c of chunks) {
      combined.set(c, offset);
      offset += c.byteLength;
    }
    return new Blob([combined], { type: "audio/mpeg" });
  }, []);

  const callSpeak = useCallback(
    async (text: string): Promise<void> => {
      const res = await fetch("/api/speak", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!res.ok || !res.body) {
        throw new Error(`TTS error ${res.status}`);
      }

      const reader = res.body.getReader();
      const chunks: Uint8Array[] = [];
      let totalLen = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) {
          chunks.push(value);
          totalLen += value.byteLength;
        }
      }
      const combined = new Uint8Array(totalLen);
      let offset = 0;
      for (const c of chunks) {
        combined.set(c, offset);
        offset += c.byteLength;
      }

      const blob = new Blob([combined], { type: "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      const ctx = await ensureAudioCtx();

      // Esperar a que el AudioContext esté completamente running
      // antes de reproducir (fix primer audio falla en móvil)
      if (ctx.state === "suspended") {
        await ctx.resume();
      }

      const audio = new Audio();
      audioElRef.current = audio;
      audio.src = url;
      audio.crossOrigin = "anonymous";
      const source = ctx.createMediaElementSource(audio);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.3;
      source.connect(analyser);
      analyser.connect(ctx.destination);
      audioAnalyserRef.current = analyser;

      return new Promise<void>((resolve) => {
        audio.onended = () => {
          URL.revokeObjectURL(url);
          audioAnalyserRef.current = null;
          resolve();
        };
        audio.onerror = () => {
          URL.revokeObjectURL(url);
          audioAnalyserRef.current = null;
          resolve();
        };
        audio.play().catch((err) => {
          console.error("Audio play error:", err);
          resolve();
        });
      });
    },
    [ensureAudioCtx]
  );

  const speak = useCallback(
    async (text: string) => {
      if (!text.trim()) return;
      setNucleusState("speaking");
      try {
        await callSpeak(text);
      } catch (err) {
        console.error("Speak error:", err);
        setError("No pude reproducir la voz.");
      } finally {
        setAudioLevel(0);
      }
    },
    [callSpeak]
  );

  const handleFollowupTimeout = useCallback(async () => {
    if (followupTimerRef.current) {
      clearTimeout(followupTimerRef.current);
      followupTimerRef.current = null;
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    commandBufferRef.current = "";
    wakeBufferRef.current = "";
    setTranscript("");
    setPhase("always");
    const farewell = pickRandom(FOLLOWUP_FAREWELLS, lastFarewellRef.current);
    lastFarewellRef.current = farewell;
    await speak(farewell);
    setNucleusState("idle");
  }, [speak]);

  const startFollowup = useCallback(() => {
    setPhase("followup");
    setNucleusState("listening");
    commandBufferRef.current = "";
    liveTranscriptRef.current = "";
    setTranscript("");
    if (followupTimerRef.current) clearTimeout(followupTimerRef.current);
    followupTimerRef.current = window.setTimeout(() => {
      handleFollowupTimeout();
    }, FOLLOWUP_TIMEOUT_MS);
  }, [handleFollowupTimeout]);

  const submitCommand = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) {
        setNucleusState("idle");
        setPhase("always");
        return;
      }
      if (submittingRef.current) return;
      submittingRef.current = true;

      // speak() (ack, despedidas...) reproduce por su cuenta en un
      // <audio> aparte del audioQueue de la respuesta real. Si seguía
      // sonando cuando llega esta respuesta, se solapan los dos audios.
      // Cortamos aquí cualquier frase predefinida pendiente.
      if (audioElRef.current) {
        try {
          audioElRef.current.pause();
        } catch {
          /* ignore */
        }
        audioElRef.current = null;
      }

      setNucleusState("processing");
      setAction(null);

      const userMsg: ChatMessage = {
        id: newId(),
        role: "user",
        content: trimmed,
      };
      const nextHistory = [...messages, userMsg];
      setMessages(nextHistory);
      setTranscript("");
      liveTranscriptRef.current = "";
      commandBufferRef.current = "";

      const audioQueue = audioQueueRef.current!;
      audioQueue.setAudioContext(
        await ensureAudioCtx(),
        (analyser) => {
          audioAnalyserRef.current = analyser;
        },
      );
      audioQueue.stop(); // Detener audio anterior si estaba sonando
      audioQueue.reset();
      const abortController = new AbortController();
      pipelineAbortRef.current = abortController;
      streamOpenRef.current = true;
      setPipelineActive(true);

      const textStream = createTextStream();
      let assistantText = "";

      // El segmenter consume el stream incrementalmente y dispara TTS
      // por cada frase. Corre en paralelo del bucle SSE.
      const segmenterPromise = (async () => {
        try {
          for await (const sentence of segmentText(textStream)) {
            if (abortController.signal.aborted) break;
            try {
              const t0 = performance.now();
              const blob = await callSpeakBlob(sentence);
              const latency = Math.round(performance.now() - t0);
              console.log(
                `[tts] sentence chars=${sentence.length} provider=elevenlabs latencyMs=${latency}`,
              );
              audioQueue.push(blob);
              // Si el SSE ya cerró pero seguimos encolando frases,
              // pasamos a "speaking" para reflejarlo en el Núcleo.
              if (!streamOpenRef.current) {
                setNucleusState("speaking");
              }
            } catch (err) {
              const message = err instanceof Error ? err.message : String(err);
              console.warn(`[tts] sentence error: ${message}`);
            }
          }
        } catch (err) {
          console.error("[segmenter] error:", err);
        }
      })();

      // Bucle principal: consume el SSE del LLM y alimenta el textStream.
      let hadError = false;
      try {
        const t0 = performance.now();
        for await (const ev of openSSE({
          url: "/api/chat",
          body: {
            messages: nextHistory
              .filter((m) => m.id !== "init")
              .map((m) => ({ role: m.role, content: m.content })),
            now: new Date().toISOString(),
            userName,
          },
          signal: abortController.signal,
        })) {
          if (ev.event === "delta") {
            const { text } = JSON.parse(ev.data) as { text: string };
            const cleaned = text.replace(/<cite[^>]*>[^<]*<\/cite>/g, "");
            assistantText += cleaned;
            textStream.push(cleaned);
          } else if (ev.event === "status") {
            const { kind } = JSON.parse(ev.data) as { kind: string };
            if (kind === "searching") {
              setNucleusState("processing");
            }
          } else if (ev.event === "tool") {
            const { name, input } = JSON.parse(ev.data) as {
              name: string;
              input: Omit<EmailAction, "type"> | Omit<CalendarAction, "type">;
            };
            if (name === "prepare_email") {
              setAction({ type: "email", ...(input as Omit<EmailAction, "type">) });
            } else if (name === "prepare_calendar_event") {
              setAction({ type: "calendar", ...(input as Omit<CalendarAction, "type">) });
            }
          } else if (ev.event === "done") {
            // cerrar stream
          } else if (ev.event === "error") {
            const { message } = JSON.parse(ev.data) as { message: string };
            throw new Error(message);
          }
        }
        if (assistantText.length === 0) {
          console.log("[pipeline] ttfAudioMs=never (no text)");
          setError("Sin respuesta del modelo");
          hadError = true;
        } else {
          const totalMs = Math.round(performance.now() - t0);
          console.log(
            `[pipeline] chat closed totalMs=${totalMs} chars=${assistantText.length}`,
          );
        }
      } catch (err) {
        hadError = true;
        const msg = err instanceof Error ? err.message : "Error desconocido";
        console.error("Submit error:", err);
        setError(msg);
        // Avisar al usuario por voz: el sistema no pudo atenderle.
        try {
          await speak(NUCLEUS_ERROR_FAREWELL);
        } catch (speakErr) {
          console.error("[submit] error speaking nucleus failure:", speakErr);
        }
      } finally {
        streamOpenRef.current = false;
        textStream.close();
        await segmenterPromise;
        // Espera a que el AudioQueue termine de sonar antes del followup
        await audioQueue.whenEmpty();

        const assistantMsg: ChatMessage = {
          id: newId(),
          role: "assistant",
          content: hadError ? NUCLEUS_ERROR_FAREWELL : (assistantText || "..."),
        };
        setMessages((prev) => [...prev, assistantMsg]);

        setPipelineActive(false);
        setAudioLevel(0);
        pipelineAbortRef.current = null;
        submittingRef.current = false;
        if (hadError) {
          // Volvemos al idle sin pedir followup: el sistema falló, no
          // tiene sentido preguntar "¿algo más?".
          setPhase("always");
          setNucleusState("idle");
        } else {
          startFollowup();
        }
      }
    },
    [callSpeakBlob, ensureAudioCtx, messages, speak, startFollowup],
  );

  const cancelCommand = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (followupTimerRef.current) {
      clearTimeout(followupTimerRef.current);
      followupTimerRef.current = null;
    }
    commandBufferRef.current = "";
    wakeBufferRef.current = "";
    setTranscript("");
    setPhase("always");
    setNucleusState("idle");
  }, []);

  const sendCurrentCommand = useCallback(async () => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    const cmd = commandBufferRef.current.trim();

    // Comandos locales de UI: no pasan por el LLM.
    if (cmd && CHAT_CLOSE_PATTERNS.some((p) => p.test(cmd))) {
      setChatOpen(false);
      cancelCommand();
      return;
    }
    if (cmd && CHAT_OPEN_PATTERNS.some((p) => p.test(cmd))) {
      setChatOpen(true);
      cancelCommand();
      return;
    }

    if (cmd) {
      void submitCommand(cmd);
      return;
    }

    // Wake detectado pero el usuario no ha dicho nada: despedida
    // breve antes de soltar la escucha.
    const farewell = pickRandom(IDLE_FAREWELLS, lastFarewellRef.current);
    lastFarewellRef.current = farewell;
    await speak(farewell);
    cancelCommand();
  }, [cancelCommand, submitCommand, speak]);

  // Entra en modo comando tras detectar la wake word. Definido después de
  // sendCurrentCommand porque hace un fallback con setTimeout que lo llama.
  const enterCommandMode = useCallback(() => {
    // Si hay una pipeline en curso (GORDO hablando o pensando la
    // respuesta), la cortamos en seco para atender al usuario.
    if (pipelineAbortRef.current) {
      pipelineAbortRef.current.abort();
      pipelineAbortRef.current = null;
    }
    if (audioQueueRef.current) {
      audioQueueRef.current.stop();
    }
    streamOpenRef.current = false;
    setPipelineActive(false);
    setAudioLevel(0);

    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (wakeFlashTimerRef.current) {
      clearTimeout(wakeFlashTimerRef.current);
      wakeFlashTimerRef.current = null;
    }
    commandBufferRef.current = "";
    wakeBufferRef.current = "";
    setTranscript("");
    setPhase("command");
    setNucleusState("listening");
    setWakeFlash(true);
    wakeFlashTimerRef.current = window.setTimeout(
      () => setWakeFlash(false),
      WAKE_FLASH_MS
    );

    // Timer base por si la recognition no emite más onresult (usuario
    // completamente en silencio). Los onresult posteriores lo pisan.
    silenceTimerRef.current = window.setTimeout(() => {
      void sendCurrentCommand();
    }, COMMAND_SILENCE_MS);

    // Ack corto para que el usuario sepa que se le ha oído, aunque luego
    // no diga nada. speak() pone el Núcleo en "speaking"; al terminar el
    // TTS volvemos a "listening" si seguimos en command (no nos pisó un
    // comando real).
    void speak(WAKE_ACK).then(() => {
      if (phaseRef.current === "command") {
        setNucleusState("listening");
      }
    });
  }, [speak, sendCurrentCommand]);

  const startRecognition = useCallback((opts?: { force?: boolean }) => {
    if (document.hidden) return;
    // En móvil solo se permite arrancar desde el gesto de mantener pulsado.
    if (isMobileRef.current && !opts?.force) return;
    // Si estamos en escucha pasiva y el toggle está apagado, no
    // arrancar reconocimiento (el micro debe quedar libre). force=true
    // (pulsar-para-hablar) salta esta guarda: el usuario ya dio su
    // gesto explícito, no depende de la escucha continua.
    if (
      !opts?.force &&
      phaseRef.current === "always" &&
      !alwaysListenRef.current
    )
      return;
    const SR =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      console.warn("[GORDO] SpeechRecognition no soportado");
      return;
    }
    if (recognitionRef.current) return;
    if (mutedRef.current) return;
    if (
      !opts?.force &&
      (nucleusStateRef.current === "processing" ||
        nucleusStateRef.current === "speaking")
    )
      return;

    const r = new SR();
    r.lang = SPEECH_LANG;
    r.continuous = true;
    r.interimResults = true;
    r.maxAlternatives = 1;

    r.onresult = (event) => {
      // Si el sistema está pensando o hablando, ignorar
      if (
        nucleusStateRef.current === "processing" ||
        nucleusStateRef.current === "speaking"
      )
        return;

      let finalText = "";
      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) finalText += res[0].transcript;
        else interimText += res[0].transcript;
      }
      // Los resultados provisionales se actualizan varias veces mientras el
      // usuario habla. Solo los finales deben entrar en el buffer; si no,
      // cada actualización vuelve a añadir la misma frase.
      const finalIncoming = finalText.trim();
      const interimIncoming = interimText.trim();
      if (!finalIncoming && !interimIncoming) return;

      console.log(
        "[GORDO] heard:",
        JSON.stringify(finalIncoming || interimIncoming),
        "phase=",
        phaseRef.current
      );

      const inCommand =
        phaseRef.current === "command" ||
        phaseRef.current === "followup";

      if (!inCommand && !isMobileRef.current) {
        // Modo escucha continua: detectar wake word
        // En móvil no detectamos wake word - solo PTT
        const committedWake = (
          wakeBufferRef.current + " " + finalIncoming
        )
          .toLowerCase()
          .trim()
          .slice(-300);
        const wakeCandidate = (
          committedWake + " " + interimIncoming
        ).trim();
        wakeBufferRef.current = committedWake;
        if (detectWakeWord(wakeCandidate)) {
          console.log(
            "[GORDO] wake word detected in buffer:",
            wakeCandidate
          );
          // Limpiar lo que vino antes de la wake word para no contaminar
          // el comando con texto residual
          const matched = WAKE_PATTERNS.find((p) =>
            p.test(wakeCandidate)
          )!;
          const idx = wakeCandidate.search(matched);
          // No conservamos el texto provisional posterior a la wake word:
          // volverá como resultado final en el siguiente evento.
          const committedAfterWake =
            idx < committedWake.length
              ? committedWake.slice(idx).replace(matched, "").trim()
              : "";
          wakeBufferRef.current = committedAfterWake;
          commandBufferRef.current = wakeBufferRef.current;
          wakeBufferRef.current = "";
          liveTranscriptRef.current = commandBufferRef.current;
          setTranscript(commandBufferRef.current);
          enterCommandMode();
        }
      } else {
        // Modo comando / follow-up: acumular y armar timer de silencio
        if (finalIncoming) {
          commandBufferRef.current = (
            commandBufferRef.current + " " + finalIncoming
          ).trim();
        }
        liveTranscriptRef.current = (
          commandBufferRef.current + " " + interimIncoming
        ).trim();
        setTranscript(liveTranscriptRef.current);
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = null;
        }
        // El usuario ya soltó el botón PTT y estábamos esperando este
        // resultado final para mandar: mandamos ya, sin esperar al
        // timer de silencio (que ni siquiera hemos vuelto a armar).
        if (pttPendingSendRef.current && finalIncoming) {
          pttPendingSendRef.current = false;
          if (pttWatchdogTimerRef.current) {
            clearTimeout(pttWatchdogTimerRef.current);
            pttWatchdogTimerRef.current = null;
          }
          sendCurrentCommand();
          return;
        }
        // Si estamos en follow-up, cancelar el timer de 10s: el usuario
        // ha hablado, no vamos a decir la despedida.
        if (
          phaseRef.current === "followup" &&
          followupTimerRef.current
        ) {
          clearTimeout(followupTimerRef.current);
          followupTimerRef.current = null;
        }
        silenceTimerRef.current = window.setTimeout(() => {
          sendCurrentCommand();
        }, COMMAND_SILENCE_MS);
      }
    };

    r.onerror = (e) => {
      console.log("[GORDO] recognition error:", e.error);
      if (
        e.error === "aborted"
      )
        return;
    };

    r.onend = () => {
      recognitionRef.current = null;
      // Red de seguridad: si soltamos el botón PTT y stop() cerró la
      // sesión sin darnos un resultado final (p.ej. el usuario no dijo
      // nada, o el final ya había llegado antes), mandamos igualmente
      // con lo que haya en el buffer y NO reiniciamos el reconocimiento.
      if (pttPendingSendRef.current) {
        pttPendingSendRef.current = false;
        if (pttWatchdogTimerRef.current) {
          clearTimeout(pttWatchdogTimerRef.current);
          pttWatchdogTimerRef.current = null;
        }
        sendCurrentCommand();
        return;
      }
      // Re-arrancar si sigue habilitado y la pestaña está visible.
      // Importante: comprobar también alwaysListen — si estamos en phase
      // "always" con el toggle apagado, NO re-arrancar (sino el micro
      // se reactiva solo al cerrarse cada sesión).
      // En móvil no re-arrancamos: el PTT controla inicio/fin, y un
      // auto-restart tras soltar el botón causa un bucle de reconocimiento.
      const shouldListen =
        phaseRef.current !== "always" || alwaysListenRef.current;
      if (
        shouldListen &&
        !mutedRef.current &&
        !document.hidden &&
        nucleusStateRef.current !== "processing" &&
        nucleusStateRef.current !== "speaking" &&
        !isMobileRef.current
      ) {
        setTimeout(() => {
          if (
            shouldListen &&
            !mutedRef.current &&
            !document.hidden &&
            nucleusStateRef.current !== "processing" &&
            nucleusStateRef.current !== "speaking" &&
            !isMobileRef.current &&
            !recognitionRef.current
          ) {
            startRecognition();
          }
        }, 400);
      }
    };

    recognitionRef.current = r;
    try {
      console.log("[GORDO] calling r.start()");
      r.start();
      console.log("[GORDO] r.start() returned");
    } catch (err) {
      console.error("[GORDO] recognition start error:", err);
    }
  }, [enterCommandMode, sendCurrentCommand]);

  // Pulsar-para-hablar: como enterCommandMode pero sin el ack hablado (el
  // usuario ya dio un gesto explícito, no necesita oír "Dime.") y
  // arrancando el reconocimiento en el mismo gesto síncrono. Diferirlo a
  // un efecto posterior —como hacía enterCommandMode vía setPhase— pierde
  // el contexto del toque en móvil y las primeras palabras si el usuario
  // habla nada más apretar.
  const startPtt = useCallback(() => {
    if (pipelineAbortRef.current) {
      pipelineAbortRef.current.abort();
      pipelineAbortRef.current = null;
    }
    if (audioQueueRef.current) {
      audioQueueRef.current.stop();
    }
    streamOpenRef.current = false;
    setPipelineActive(false);
    setAudioLevel(0);

    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (wakeFlashTimerRef.current) {
      clearTimeout(wakeFlashTimerRef.current);
      wakeFlashTimerRef.current = null;
    }
    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
      } catch {
        /* ignore */
      }
      recognitionRef.current = null;
    }
    commandBufferRef.current = "";
    wakeBufferRef.current = "";
    setTranscript("");
    pttPendingSendRef.current = false;
    // Refs síncronos primero: gobiernan las guardas de startRecognition
    // y de onresult, y deben leer bien ya en este mismo tick (el state
    // de React no se refleja en los refs hasta el próximo render).
    phaseRef.current = "command";
    nucleusStateRef.current = "listening";
    setPhase("command");
    setNucleusState("listening");
    startRecognition({ force: true });
  }, [startRecognition]);

  // Asegurar reconocimiento activo cuando proceda.
  // Importante: NO abortar en cleanup. Chrome sólo permite un
  // SpeechRecognition activo; abortar y crear otro sincrónicamente
  // provoca que Chrome aborte el nuevo -> bucle.
  // El rearme lo gestiona r.onend con debounce.
  useEffect(() => {
    if (!supported) return;
    if (!activated) return;
    // En móvil el reconocimiento solo se inicia desde startPtt().
    if (isMobileRef.current) return;
    if (muted) return;
    if (document.hidden) return;
    if (isOffline) return;
    // Si estamos en escucha pasiva (phase=always) y el toggle de wake
    // word está apagado, paramos el reconocimiento activo.
    if (phase === "always" && !alwaysListen) {
      const r = recognitionRef.current;
      if (r) {
        try {
          r.abort();
        } catch {
          /* ignore */
        }
        recognitionRef.current = null;
      }
      return;
    }
    if (
      nucleusState === "processing" ||
      nucleusState === "speaking"
    )
      return;
    if (recognitionRef.current) return;
    startRecognition();
  }, [
    supported,
    activated,
    muted,
    isOffline,
    phase,
    alwaysListen,
    nucleusState,
    startRecognition,
  ]);

  // Silenciar reconocimiento mientras GORDO habla o piensa
  useEffect(() => {
    if (nucleusState === "processing" || nucleusState === "speaking") {
      const r = recognitionRef.current;
      if (r) {
        try {
          r.abort();
        } catch {
          /* ignore */
        }
        recognitionRef.current = null;
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    }
  }, [nucleusState]);

  // Escape: cancelar comando o follow-up en curso
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (
        e.key === "Escape" &&
        (phaseRef.current === "command" ||
          phaseRef.current === "followup")
      ) {
        e.preventDefault();
        cancelCommand();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cancelCommand]);

  // Pausar todo cuando la pestaña no está visible (evita que Chrome
  // robe el foco y mantiene el micro liberado)
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) {
        const r = recognitionRef.current;
        if (r) {
          try {
            r.abort();
          } catch {
            /* ignore */
          }
          recognitionRef.current = null;
        }
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = null;
        }
        if (followupTimerRef.current) {
          clearTimeout(followupTimerRef.current);
          followupTimerRef.current = null;
        }
        commandBufferRef.current = "";
        wakeBufferRef.current = "";
        stopMic();
        setMicLevel(0);
      } else if (
        supported &&
        !mutedRef.current &&
        !offlineRef.current &&
        (phaseRef.current !== "always" || alwaysListenRef.current) &&
        !isMobileRef.current &&
        nucleusStateRef.current !== "processing" &&
        nucleusStateRef.current !== "speaking"
      ) {
        startRecognition();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [supported, startRecognition, stopMic]);

  // Mic persistente para visualización en modo comando
  useEffect(() => {
    if (phase === "command") {
      void startMic();
    } else {
      stopMic();
      setMicLevel(0);
    }
  }, [phase, startMic, stopMic]);

  // Asegurar liberación de recursos al desmontar
  useEffect(() => {
    return () => {
      stopMic();
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch {
          /* ignore */
        }
      }
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      if (wakeFlashTimerRef.current) clearTimeout(wakeFlashTimerRef.current);
      if (pttWatchdogTimerRef.current) clearTimeout(pttWatchdogTimerRef.current);
      stopLevelLoop();
      if (audioElRef.current) {
        audioElRef.current.pause();
        audioElRef.current = null;
      }
      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        audioCtxRef.current.close().catch(() => {});
      }
    };
  }, [stopLevelLoop, stopMic]);

  const onPressStart = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      // Solo iniciar PTT si estamos completamente libres:
      // - phase "always" (no en comando activo)
      // - nucleus "idle" (no procesando ni hablando)
      // Esto evita que múltiples comandos se envíen mientras GORDO responde.
      if (
        phaseRef.current === "always" &&
        (nucleusStateRef.current === "idle" ||
          nucleusStateRef.current === "offline")
      ) {
        startPtt();
      }
      // Si ya está en comando o GORDO está activo, no hacer nada (esperar release)
    },
    [startPtt]
  );

  const onPressEnd = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      try {
        (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      if (phaseRef.current !== "command") return;
      const r = recognitionRef.current;
      if (!r) {
        // No llegó a arrancar (permiso denegado, etc.): mandamos lo que
        // haya, probablemente vacío.
        sendCurrentCommand();
        return;
      }
      // Puede haber quedado armado un timer de "2s de silencio" de un
      // resultado final anterior mientras el usuario aún hablaba (antes
      // de soltar). Si no lo cancelamos, dispara sendCurrentCommand()
      // una segunda vez ~2s después, ya con el buffer vacío (lo vació
      // el envío bueno) -> suelta la despedida encima de la respuesta
      // real.
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
      // No enviamos ya mismo: esperamos al resultado final pendiente
      // (stop() lo entrega tras cerrar) para no perder la última
      // palabra dicha justo al soltar. onresult u onend disparan el
      // envío real (ver startRecognition). Watchdog por si ninguno de
      // los dos llega.
      pttPendingSendRef.current = true;
      pttWatchdogTimerRef.current = window.setTimeout(() => {
        if (pttPendingSendRef.current) {
          pttPendingSendRef.current = false;
          pttWatchdogTimerRef.current = null;
          sendCurrentCommand();
        }
      }, 2500);
      try {
        r.stop();
      } catch {
        pttPendingSendRef.current = false;
        if (pttWatchdogTimerRef.current) {
          clearTimeout(pttWatchdogTimerRef.current);
          pttWatchdogTimerRef.current = null;
        }
        sendCurrentCommand();
      }
    },
    [sendCurrentCommand]
  );

  const statusText = (() => {
    if (isOffline) return "Sin conexión";
    if (error) return "Error";
    if (pipelineActive) {
      const speaking =
        audioQueueRef.current && !audioQueueRef.current.isEmpty();
      return speaking ? "Hablando" : "Pensando";
    }
    if (nucleusState === "speaking") return "Hablando";
    if (nucleusState === "processing") return "Pensando";
    if (muted) return "Silenciado";
    if (phase === "command") return "Te escucho";
    if (phase === "followup") return "¿Algo más?";
    if (wakeFlash) return `¡${ASSISTANT_NAME}!`;
    if (phase === "always" && !alwaysListen) return "Pulsa para hablar";
    return `En escucha · di "${ASSISTANT_NAME}"`;
  })();

  const statusColor = isOffline
    ? "bg-cyan-dim"
    : error
      ? "bg-magenta"
      : nucleusState === "speaking"
        ? "bg-cyan-bright"
        : nucleusState === "processing"
          ? "bg-cyan"
          : phase === "command" || phase === "followup"
            ? "bg-cyan"
            : muted
              ? "bg-text-faint"
              : phase === "always" && !alwaysListen
                ? "bg-text-faint"
                : "bg-cyan/50";

  return (
    <main className="relative min-h-screen w-full w-full overflow-hidden bg-bg text-text">
      <HudBrackets />
      {nameRequired && (
        <div className="pointer-events-auto absolute inset-0 z-50 flex items-center justify-center bg-bg/85 backdrop-blur-md">
          <div className="max-w-md px-6 text-center">
            <p className="mb-3 font-mono text-[10px] tracking-[0.24em] text-cyan-dim uppercase">
              {ASSISTANT_NAME}
            </p>
            <h1 className="mb-4 font-display text-2xl leading-tight text-text sm:text-3xl">
              ¿Cómo te llamas?
            </h1>
            <p className="mb-6 text-sm leading-relaxed text-text-dim">
              Dime tu nombre para que pueda tutearte.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const trimmed = nameInput.trim();
                if (!trimmed) return;
                setUserName(trimmed);
                localStorage.setItem("rigodon:userName", trimmed);
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === "init"
                      ? {
                          ...m,
                          content: `Buenas, ${trimmed}. Cuando quieras, di "${ASSISTANT_NAME}" o pulsa el botón.`,
                        }
                      : m
                  )
                );
                setNameRequired(false);
              }}
              className="flex flex-col items-center gap-4"
            >
              <input
                type="text"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                placeholder="Tu nombre"
                autoFocus
                className="w-full max-w-xs rounded-full border border-cyan/40 bg-cyan/10 px-6 py-3 font-mono text-center text-sm text-text placeholder:text-text-faint focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/30"
              />
              <button
                type="submit"
                disabled={!nameInput.trim()}
                className="cursor-pointer rounded-full border border-cyan/40 bg-cyan/10 px-8 py-3 font-mono text-[11px] tracking-[0.20em] text-cyan uppercase transition hover:border-cyan-bright hover:bg-cyan/20 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Continuar
              </button>
            </form>
          </div>
        </div>
      )}
      {!activated && !nameRequired && (
        <div className="pointer-events-auto absolute inset-0 z-50 flex items-center justify-center bg-bg/85 backdrop-blur-md">
          <div className="max-w-md px-6 text-center">
            <p className="mb-3 font-mono text-[10px] tracking-[0.24em] text-cyan-dim uppercase">
              {ASSISTANT_NAME}
            </p>
            <h1 className="mb-4 font-display text-2xl leading-tight text-text sm:text-3xl">
              Hola, {userName}
            </h1>
            <p className="mb-8 text-sm leading-relaxed text-text-dim">
              Pulsa el botón grande para hablar con {ASSISTANT_NAME}, o activa
              la escucha continua arriba a la derecha (recomendado en escritorio).
            </p>
            <button
              onClick={() => setActivated(true)}
              className="cursor-pointer rounded-full border border-cyan/40 bg-cyan/10 px-8 py-3 font-mono text-[11px] tracking-[0.20em] text-cyan uppercase transition hover:border-cyan-bright hover:bg-cyan/20"
            >
              Empezar a escuchar
            </button>
          </div>
        </div>
      )}
      <Radar
        state={nucleusState}
        micLevel={micLevel}
        audioLevel={audioLevel}
      />

      <div className="pointer-events-none absolute inset-0 z-10 flex flex-col">
        {/* HUD corner panels — desktop only */}
        <div className="pointer-events-none hidden lg:block">
          <HudPanel
            label="SESSION"
            rightLabel="028·ARC"
            className="absolute left-6 top-24 w-56"
          >
            <div className="flex justify-between font-mono text-[10px] text-text-dim">
              <span>{ASSISTANT_NAME} v0.1</span>
              <span className="text-cyan/70">ONLINE</span>
            </div>
          </HudPanel>
          <HudPanel
            label="STATUS · READY"
            rightLabel={`${Math.round((micLevel + audioLevel) * 50)}%`}
            className="absolute right-6 top-24 w-56"
          >
            <HudBar value={Math.min(1, micLevel + audioLevel)} />
          </HudPanel>
          <HudPanel
            label="SCAN"
            rightLabel={`${Math.round(audioLevel * 100)}%`}
            className="absolute bottom-32 left-6 w-56"
          >
            <HudBar value={audioLevel} />
          </HudPanel>
          <HudPanel
            label="STREAM"
            rightLabel="··"
            className="absolute bottom-32 right-6 w-56"
          >
            <div className="font-mono text-[10px] leading-relaxed text-cyan/70">
              <div>{`>> 74282111145680`}</div>
              <div>{`>> ${ASSISTANT_NAME.toUpperCase()} · READY`}</div>
              <div className="text-text-dim">{`>> ${new Date().toLocaleTimeString("es-ES")}`}</div>
            </div>
          </HudPanel>
        </div>

        {/* HUD strips — mobile/tablet */}
        <div className="pointer-events-none block lg:hidden">
          <div className="absolute left-0 right-0 top-16 flex justify-center">
            <span className="font-mono text-[10px] tracking-[0.24em] text-cyan-dim uppercase">
              {`SESSION 028 · ${ASSISTANT_NAME.toUpperCase()}`}
            </span>
          </div>
          <div className="absolute bottom-4 left-4 right-4">
            <div className="rounded-sm border border-line bg-[rgba(3,22,30,0.55)] px-3 py-2 font-mono text-[10px] tracking-[0.18em] text-cyan/70 uppercase backdrop-blur-sm">
              {`>> ${ASSISTANT_NAME.toUpperCase()} · READY · ${new Date().toLocaleTimeString("es-ES")}`}
            </div>
          </div>
        </div>

        <header className="flex items-center justify-between px-6 py-5 sm:px-10 sm:py-7">
          <div className="flex items-center gap-3">
            <div className="h-1.5 w-1.5 rounded-full bg-cyan shadow-[0_0_8px_rgba(0,229,255,0.5)]" />
            <span className="font-display text-sm tracking-[0.18em] text-text-dim uppercase">
              {ASSISTANT_NAME}
            </span>
          </div>

          <motion.div
            animate={
              wakeFlash
                ? { scale: [1, 1.08, 1], borderColor: ["rgba(0,229,255,0.18)", "rgba(0,229,255,0.8)", "rgba(0,229,255,0.18)"] }
                : { scale: 1, borderColor: "rgba(0,229,255,0.18)" }
            }
            transition={{ duration: WAKE_FLASH_MS / 1000, ease: "easeOut" }}
            className="flex items-center gap-2.5 rounded-full border border-line bg-panel/60 px-4 py-1.5 backdrop-blur-sm"
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${statusColor} transition-colors duration-200`}
              style={{
                boxShadow:
                  phase === "command" || nucleusState === "speaking"
                    ? "0 0 8px currentColor"
                    : "none",
              }}
            />
            <span className="font-mono text-[11px] tracking-[0.14em] text-text-dim uppercase">
              {statusText}
            </span>
          </motion.div>

          <div className="flex items-center gap-2">
            <InstallButton />
            <button
              type="button"
              onClick={() => setChatOpen((c) => !c)}
              aria-label={chatOpen ? "Ocultar chat" : "Mostrar chat"}
              title={chatOpen ? "Ocultar chat" : "Mostrar chat"}
              className={`pointer-events-auto flex h-7 w-7 items-center justify-center rounded-full border backdrop-blur-sm transition ${
                chatOpen
                  ? "border-cyan/40 bg-cyan/10 text-cyan shadow-[0_0_10px_rgba(0,229,255,0.25)]"
                  : "border-line bg-[rgba(3,22,30,0.6)] text-text-dim hover:border-cyan/40"
              }`}
            >
              {chatOpen ? <ChatIconOn /> : <ChatIconOff />}
            </button>
            {!isMobile && (
              <button
                onClick={() => {
                  setAlwaysListen((a) => {
                    const next = !a;
                    localStorage.setItem(
                      "rigodon:alwaysListen:user",
                      String(next),
                    );
                    return next;
                  });
                }}
                aria-label={
                  alwaysListen
                    ? "Desactivar escucha continua"
                    : "Activar escucha continua (wake word)"
                }
                title={
                  alwaysListen
                    ? "Escucha continua activa · pulsa para apagarla"
                    : "Escucha continua apagada · pulsa para activarla"
                }
                className={`pointer-events-auto flex h-7 w-7 items-center justify-center rounded-full border backdrop-blur-sm transition ${
                  alwaysListen
                    ? "border-cyan/40 bg-cyan/10 text-cyan shadow-[0_0_10px_rgba(0,229,255,0.25)]"
                    : "border-line bg-[rgba(3,22,30,0.6)] text-text-dim hover:border-cyan/40"
                }`}
              >
                <RippleIcon active={alwaysListen} />
              </button>
            )}
            <button
              onClick={() => setMuted((m) => !m)}
              aria-label={muted ? "Activar micrófono" : "Silenciar"}
              title={muted ? "Activar micrófono" : "Silenciar"}
              className="pointer-events-auto flex h-7 w-7 items-center justify-center rounded-full border border-line bg-[rgba(3,22,30,0.6)] text-text-dim backdrop-blur-sm transition hover:border-cyan/40 hover:text-cyan"
            >
              {muted ? <MicOffIcon /> : <MicOnIcon />}
            </button>
            <span className="font-display text-sm tracking-[0.18em] text-text-faint uppercase">
              {userName}
            </span>
          </div>
        </header>

        <div className="flex flex-1 flex-col-reverse lg:flex-row">
          <section className="pointer-events-auto flex flex-1 flex-col items-center justify-end gap-6 px-6 pb-32 pt-12 sm:pb-40">
            <div className="text-center">
              <p className="font-mono text-[11px] tracking-[0.20em] text-text-faint uppercase">
                {muted
                  ? "Silenciado · pulsa el icono del micro arriba"
                  : isOffline
                    ? "Sin conexión · esperando la red"
                    : nucleusState === "speaking"
                      ? `${ASSISTANT_NAME} está hablando`
                      : nucleusState === "processing"
                        ? "Procesando"
                        : phase === "followup"
                          ? "¿Algo más? · 2s para enviar · 10s para cerrar"
                          : phase === "command"
                            ? isMobile
                              ? "Mantén pulsado para hablar · suelta para enviar"
                              : "Escuchando tu orden · 2s de silencio para enviar"
                            : phase === "always" && !alwaysListen
                              ? "Mantén pulsado el botón para hablar"
                              : `Di "${ASSISTANT_NAME}" para activarme`}
              </p>
            </div>

            <PttButton
              disabled={
                isOffline ||
                nucleusState === "processing" ||
                nucleusState === "speaking"
              }
              mode={phase === "command" ? "active" : "idle"}
              onPointerDown={onPressStart}
              onPointerUp={onPressEnd}
              onPointerCancel={onPressEnd}
              onPointerLeave={onPressEnd}
            />

          </section>

          <aside
            className={`pointer-events-auto hidden w-full flex-col border-t border-line bg-panel/40 backdrop-blur-md ${
              chatOpen ? "lg:flex" : ""
            } lg:w-[400px] lg:border-l lg:border-t-0 xl:w-[440px]`}
          >
            <div className="flex items-center justify-between border-b border-line px-5 py-4">
              <span className="font-display text-xs tracking-[0.2em] text-text-dim uppercase">
                Conversación
              </span>
              <span className="font-mono text-[10px] tracking-[0.18em] text-text-faint uppercase">
                {messages.length} {messages.length === 1 ? "mensaje" : "mensajes"}
              </span>
            </div>

            <div
              className="flex-1 overflow-y-auto px-5 py-5"
              style={{ maxHeight: "40vh" }}
            >
              <div className="flex flex-col gap-4">
                {messages.map((m) => (
                  <MessageBubble key={m.id} message={m} userName={userName} />
                ))}
              </div>
            </div>

            <AnimatePresence>
              {action && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                  className="border-t border-line bg-panel/80 p-4 backdrop-blur-md"
                >
                  <ActionCard
                    action={action}
                    onConfirm={() => {
                      if (action.type === "email") {
                        window.location.href = buildMailto(action);
                      } else {
                        window.open(
                          buildCalendarUrl(action),
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }
                      setAction(null);
                    }}
                    onCancel={() => setAction(null)}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </aside>
        </div>
      </div>
    </main>
  );
}

function MessageBubble({
  message,
  userName,
}: {
  message: ChatMessage;
  userName: string;
}) {
  const isUser = message.role === "user";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className={`flex flex-col ${isUser ? "items-end" : "items-start"}`}
    >
      <span className="mb-1 font-mono text-[10px] tracking-[0.24em] text-cyan-dim uppercase">
        {isUser ? userName : ASSISTANT_NAME}
      </span>
      <div
        className={`max-w-[88%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
          isUser
            ? "rounded-br-sm border border-cyan/25 bg-cyan/[0.05] text-text"
            : "rounded-bl-sm border border-line bg-[rgba(3,22,30,0.6)] text-text"
        }`}
      >
        {message.content}
      </div>
    </motion.div>
  );
}

function ActionCard({
  action,
  onConfirm,
  onCancel,
}: {
  action: AssistantAction;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const isEmail = action.type === "email";
  return (
    <div className="rounded-xl border border-line bg-[rgba(3,22,30,0.85)] p-4 shadow-[0_0_24px_rgba(0,229,255,0.10)]">
      <div className="mb-3 flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-cyan shadow-[0_0_6px_rgba(0,229,255,0.6)]" />
        <span className="font-mono text-[10px] tracking-[0.24em] text-cyan uppercase">
          {isEmail ? "Correo listo" : "Evento listo"}
        </span>
      </div>
      {isEmail ? (
        <div className="mb-4 space-y-1.5 text-sm">
          <p className="text-text-dim">
            <span className="text-text-faint">Para: </span>
            {action.to}
          </p>
          <p className="text-text">
            <span className="text-text-faint">Asunto: </span>
            {action.subject}
          </p>
          <p className="line-clamp-3 text-text-dim">{action.body}</p>
        </div>
      ) : (
        <div className="mb-4 space-y-1.5 text-sm">
          <p className="text-text">{action.title}</p>
          <p className="text-text-dim">
            <span className="text-text-faint">Cuándo: </span>
            {action.startDate} → {action.endDate}
          </p>
          {action.location && (
            <p className="text-text-dim">
              <span className="text-text-faint">Dónde: </span>
              {action.location}
            </p>
          )}
        </div>
      )}
      <div className="flex gap-2">
        <button
          onClick={onConfirm}
          className="flex-1 cursor-pointer rounded-lg border border-cyan/40 bg-cyan/10 px-3 py-2 font-mono text-[11px] tracking-[0.16em] text-cyan uppercase transition hover:bg-cyan/20"
        >
          Confirmar
        </button>
        <button
          onClick={onCancel}
          className="cursor-pointer rounded-lg border border-line bg-[rgba(3,22,30,0.6)] px-3 py-2 font-mono text-[11px] tracking-[0.16em] text-text-dim uppercase transition hover:border-cyan/30 hover:text-text"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

function MicOnIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="3" width="6" height="12" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
    </svg>
  );
}

function MicOffIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="3" width="6" height="12" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <line x1="3" y1="3" x2="21" y2="21" />
    </svg>
  );
}

function RippleIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="6" strokeWidth="1.6" opacity={active ? 0.85 : 0.4} />
      <circle
        cx="12"
        cy="12"
        r="10"
        strokeWidth="1.2"
        opacity={active ? 0.45 : 0.2}
      />
    </svg>
  );
}

// Bocadillo de chat relleno (chat visible).
function ChatIconOn() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path
        d="M4 5h16v11H8l-4 4z"
        strokeWidth="1.6"
        fill="currentColor"
        fillOpacity="0.18"
      />
    </svg>
  );
}

// Bocadillo de chat tachado (chat oculto). Indicación de "mostrar chat".
function ChatIconOff() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path
        d="M4 5h16v11H8l-4 4z"
        strokeWidth="1.6"
        opacity="0.5"
      />
      <path d="M3 3l18 18" strokeWidth="1.6" />
    </svg>
  );
}
